export const APP_TYPES = ['trade_bridge', 'trade_bots', 'trade_builder', 'trade_scalper', 'equity_rebalancer', 'trade_disc'] as const;
export const DESKTOP_APP_TYPES = ['trade_bots_desktop', 'trade_disc_desktop', 'trade_scalper_desktop'] as const;
export const SUBSCRIPTION_APP_TYPES = [...APP_TYPES, ...DESKTOP_APP_TYPES] as const;

// External server-to-server products (Trade CT). ADMIN PAGES ONLY — these must never be
// rendered on any client-facing surface; users are not told they have access (owner decision,
// 2026-07-23). Client surfaces keep iterating SUBSCRIPTION_APP_TYPES.
export const EXTERNAL_APP_TYPES = ['copy_trading'] as const;
export const ADMIN_SUBSCRIPTION_APP_TYPES = [...SUBSCRIPTION_APP_TYPES, ...EXTERNAL_APP_TYPES] as const;

export type AppType = (typeof APP_TYPES)[number];
export type DesktopAppType = (typeof DESKTOP_APP_TYPES)[number];
export type SubscriptionAppType = (typeof SUBSCRIPTION_APP_TYPES)[number];
export type AdminSubscriptionAppType = (typeof ADMIN_SUBSCRIPTION_APP_TYPES)[number];

export type AppConfig = {
  appType: AppType;
  appName: string;
  accentColor: string;
  pathPrefix: string;
  title: string;
};

export const APP_CONFIGS: Record<AppType, AppConfig> = {
  trade_bridge: {
    appType: 'trade_bridge',
    appName: 'TradeBridge',
    accentColor: '#3b82f6',
    pathPrefix: '/trade-bridge',
    title: 'TradeBridge — Bluechip Algos',
  },
  trade_bots: {
    appType: 'trade_bots',
    appName: 'TradeBots',
    accentColor: '#f59e0b',
    pathPrefix: '/trade-bots',
    title: 'TradeBots — Bluechip Algos',
  },
  trade_builder: {
    appType: 'trade_builder',
    appName: 'TradeBuilder',
    accentColor: '#8b5cf6',
    pathPrefix: '/trade-builder',
    title: 'TradeBuilder — Bluechip Algos',
  },
  trade_scalper: {
    appType: 'trade_scalper',
    appName: 'TradeScalper',
    accentColor: '#10b981',
    pathPrefix: '/trade-scalper',
    title: 'TradeScalper — Bluechip Algos',
  },
  equity_rebalancer: {
    appType: 'equity_rebalancer',
    appName: 'Equity Rebalancer',
    accentColor: '#0ea5e9',
    pathPrefix: '/equity-rebalancer',
    title: 'Equity Rebalancer — Bluechip Algos',
  },
  trade_disc: {
    appType: 'trade_disc',
    appName: 'Trade Disc',
    accentColor: '#ef4444',
    pathPrefix: '/trade-disc',
    title: 'Trade Disc — Bluechip Algos',
  },
};

export type SubscriptionConfig = {
  appType: AdminSubscriptionAppType;
  appName: string;
  group: 'Web' | 'Desktop' | 'External';
};

export const SUBSCRIPTION_CONFIGS: Record<AdminSubscriptionAppType, SubscriptionConfig> = {
  trade_bridge: { appType: 'trade_bridge', appName: 'TradeBridge Web', group: 'Web' },
  trade_bots: { appType: 'trade_bots', appName: 'TradeBots Web', group: 'Web' },
  trade_builder: { appType: 'trade_builder', appName: 'TradeBuilder Web', group: 'Web' },
  trade_scalper: { appType: 'trade_scalper', appName: 'TradeScalper Web', group: 'Web' },
  equity_rebalancer: { appType: 'equity_rebalancer', appName: 'Equity Rebalancer Web', group: 'Web' },
  // "Trade Disc Web" on purpose: the frozen desktop SKU "Trade Disc Desktop" sits beside it on
  // every admin/payment surface, and the two must read distinctly.
  trade_disc: { appType: 'trade_disc', appName: 'Trade Disc Web', group: 'Web' },
  trade_bots_desktop: { appType: 'trade_bots_desktop', appName: 'Trade Bots Desktop', group: 'Desktop' },
  trade_disc_desktop: { appType: 'trade_disc_desktop', appName: 'Trade Disc Desktop', group: 'Desktop' },
  trade_scalper_desktop: { appType: 'trade_scalper_desktop', appName: 'Trade Scalper Desktop', group: 'Desktop' },
  copy_trading: { appType: 'copy_trading', appName: 'Copy Trading (Trade CT)', group: 'External' },
};

export function deriveAppTypeFromPath(pathname: string): AppType | null {
  if (pathname.startsWith('/trade-bridge')) return 'trade_bridge';
  if (pathname.startsWith('/trade-bots')) return 'trade_bots';
  if (pathname.startsWith('/trade-builder')) return 'trade_builder';
  if (pathname.startsWith('/trade-scalper')) return 'trade_scalper';
  if (pathname.startsWith('/equity-rebalancer')) return 'equity_rebalancer';
  if (pathname.startsWith('/trade-disc')) return 'trade_disc';
  return null;
}

export function getAppConfig(appType: AppType): AppConfig {
  return APP_CONFIGS[appType];
}

export function getAppPrefix(appType: AppType): string {
  return APP_CONFIGS[appType].pathPrefix;
}
